// # 사이드바 보기 — 정렬·안 읽은 것만. 다크 모드·표시처럼 이 기기의 것이다(docs/design3.md G25)
//
// 방(사이드바 머리의 "안 읽은 것만")과 설정(사이드바 탭의 정렬)이 같은 값을 고친다. 둘은 한 창 안에서
// 동시에 떠 있을 수 있어서 모듈 하나가 값을 쥐고 듣는 이들에게 알린다(`useSyncExternalStore`). 기기에는
// 표시와 같은 낱말 줄(`alpha unread`)로 둔다. 저장은 던질 수 있다 — 못 적으면 이 창에서만 산다.

import type { Keeper } from '@maru/platform'
import {
  read_sidebar_view,
  SIDEBAR_VIEW_DEFAULT,
  type SidebarView,
  sidebar_view_words,
} from '@maru/ui'
import { useEffect, useSyncExternalStore } from 'react'

export const SIDEBAR_VIEW_KEY = 'maru.sidebar'

let now: SidebarView = SIDEBAR_VIEW_DEFAULT
let loaded = false
const ears = new Set<() => void>()
const tell = () => {
  for (const ear of ears) ear()
}

export async function load_sidebar_view(keep: Keeper): Promise<SidebarView> {
  try {
    return read_sidebar_view(await keep.get(SIDEBAR_VIEW_KEY))
  } catch {
    return SIDEBAR_VIEW_DEFAULT
  }
}

export function set_sidebar_view(keep: Keeper | undefined, next: SidebarView): void {
  now = next
  loaded = true
  tell()
  if (keep === undefined) return
  const words = sidebar_view_words(next)
  void (words === '' ? keep.remove(SIDEBAR_VIEW_KEY) : keep.put(SIDEBAR_VIEW_KEY, words)).catch(
    () => {},
  )
}

/** 지금 보기와 고치는 손. 처음 부른 곳이 기기에서 한 번 읽어 온다. 기기 금고가 없으면 이 창에서만 산다. */
export function useSidebarView(keep: Keeper | undefined): [SidebarView, (v: SidebarView) => void] {
  const view = useSyncExternalStore(
    (ear) => {
      ears.add(ear)
      return () => ears.delete(ear)
    },
    () => now,
    () => now,
  )
  useEffect(() => {
    if (loaded || keep === undefined) return
    loaded = true
    void load_sidebar_view(keep).then((v) => {
      now = v
      tell()
    })
  }, [keep])
  return [view, (v) => set_sidebar_view(keep, v)]
}
